import { toRaw } from 'vue';
import { t } from '@/i18n/index.js';

/**
 * A request whose reply never arrives used to keep its listener in the events store for good,
 * and the page that sent it showed its loading bar forever, with no error. Each request now
 * gets a deadline: when nothing for it arrives in time, its listener is removed and is told
 * once, through the usual error path, that the request timed out.
 *
 * The deadline is the time since the request was sent or since the last reply for it, so a
 * long operation that sends progress ("intermediate") replies keeps its listener for as long
 * as the progress keeps coming.
 *
 * 180 s by default. The slowest ordinary replies are seconds (production gateway, 7 days to
 * 2026-10-23: the slowest reply event 562 ms; locally Company Stats 7.9 s under heavy load),
 * and the slowest page on record, admin Health, took 88.6 s before its 2026-10 fix. 180 s
 * leaves that twice over. A request that needs longer passes its own in the send context:
 * event.send(listener, { replyTimeoutMs: 600000 }); 0 turns the deadline off.
 */
export const DEFAULT_REPLY_TIMEOUT_MS = 180000;

// The error a timed-out request is answered with. Shaped like a server error: listeners read
// error().hint, .msg, .id or .messageId.
export const REPLY_TIMED_OUT_ID = 'RequestTimedOutId';

const timers = new Map();

/** The deadline for one listener: its own from the send context, or the default. */
export function replyTimeoutFor(listener) {
  const context = listener && listener.context;
  if (context && typeof context === 'object' && context.replyTimeoutMs !== undefined && context.replyTimeoutMs !== null) {
    const ms = Number(context.replyTimeoutMs);
    return Number.isFinite(ms) && ms >= 0 ? ms : DEFAULT_REPLY_TIMEOUT_MS;
  }
  return DEFAULT_REPLY_TIMEOUT_MS;
}

/** "180 s", or "300 ms" for a deadline under a second, which would otherwise read "0 s". */
function describeTimeout(timeoutMs) {
  return timeoutMs < 1000 ? `${timeoutMs} ms` : `${Math.round(timeoutMs / 1000)} s`;
}

/** The reply delivered to a listener whose request timed out. */
export function timedOutReply(id, timeoutMs) {
  return {
    // RcEventListener.emit looks the reply up by its class, and CompanyEvents.contains throws on
    // a missing one, which kept the error from ever reaching the listener. No event set
    // claims this class, so the reply is wrapped as a plain RcResponseEvent.
    '@class': 'RequestTimedOut',
    '@rid': id,
    eventName: 'RequestTimedOut',
    error: {
      id: REPLY_TIMED_OUT_ID,
      messageId: REPLY_TIMED_OUT_ID,
      status: '504',
      hint: t('shell_loading_taking_long'),
      msg: `No reply in ${describeTimeout(timeoutMs)}`,
    },
  };
}

/**
 * Starts (or restarts) the deadline for the request `id`. When it passes and the listener is
 * still the one in `listeners`, the listener is removed, the time-out is logged once and the
 * listener hears the timed-out reply.
 */
export function startReplyTimeout(listeners, id, listener) {
  clearReplyTimeout(id);
  const timeoutMs = replyTimeoutFor(listener);
  if (!timeoutMs) {
    return;
  }
  const timer = setTimeout(() => {
    timers.delete(id);
    // The store's state is reactive, so compare the objects behind its proxies.
    if (!isSameListener(listeners[id], listener)) {
      return;
    }
    delete listeners[id];
    const logger = (listener && listener.logger) || console;
    (logger.warn || logger.info).call(logger, `No reply to request ${id} in ${describeTimeout(timeoutMs)}; its listener was removed.`);
    if (listener && typeof listener.emit === 'function') {
      try {
        listener.emit(timedOutReply(id, timeoutMs));
      } catch (e) {
        (logger.error || logger.info).call(logger, `The listener for request ${id} failed on its time-out: ${e && e.message}`);
      }
    }
  }, timeoutMs);
  timers.set(id, timer);
}

/** Whether two references (either may be a reactive proxy) are the same listener. */
export function isSameListener(a, b) {
  return !!a && !!b && toRaw(a) === toRaw(b);
}

/** Stops the deadline for the request `id`: a reply came, or the listener was removed. */
export function clearReplyTimeout(id) {
  const timer = timers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
    timers.delete(id);
  }
}

/** How many requests are waiting on a deadline; for tests. */
export function pendingReplyTimeouts() {
  return timers.size;
}
